import clsx from 'clsx';
import {
  forwardRef,
  Children,
  type ReactNode,
  useMemo,
  cloneElement,
} from 'react';
import { useIntl } from 'react-intl';

import * as styles from './AvatarGroup.sty.js';
import { Item } from './Item.js';
import { Button } from '../../buttons/button/Button.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useRovingFocus } from '../../core/hooks/useRovingFocus.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { focusRingStatic } from '../../core/styles/focusRingStatic.sty.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { Menu } from '../../navigation/menu/Menu.js';

const COMPONENT_NAME = 'AvatarGroup';

/**
 * Accepted properties for AvatarGroup
 * @public
 */
export type AvatarGroupProps = WithChildren &
  AriaLabelingProps &
  StylingProps &
  DataTestId &
  MaskingProps &
  BehaviorTrackingProps;

const MAX_AVATARGROUP_ITEMS = 5;

/**
 * AvatarGroup component
 */
const RootAvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>(
  (props, forwardedRef) => {
    const {
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    }: AvatarGroupProps = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const intl = useIntl();
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);
    const { 'aria-label': ariaLabel, ...remainingAriaLabelingProps } =
      ariaLabelingProps;

    const { containerRef: rovingContainerRef } = useRovingFocus<HTMLDivElement>(
      {
        initialIndex: undefined,
        direction: 'horizontal',
        focusableChildrenSelector: '.rovingFocusElement',
      },
    );

    const [displayValues, additionalValues] = useMemo(() => {
      const childrenArray = Children.toArray(children);
      let displayValues: ReactNode[];
      const additionalValues: ReactNode[] = [];

      if (childrenArray.length > MAX_AVATARGROUP_ITEMS) {
        displayValues = childrenArray.splice(0, MAX_AVATARGROUP_ITEMS);
        for (let i = 0; i < childrenArray.length; i++) {
          const child = childrenArray[i];
          if (isSlot(child, Item)) {
            additionalValues.push(
              cloneElement(
                <Item
                  abbreviation={child.props.abbreviation}
                  tooltipText={child.props.tooltipText}
                />,
                {
                  ...child.props,
                  key: i,
                  isCollapsed: true,
                },
              ),
            );
          }
        }
      } else {
        displayValues = childrenArray;
      }

      return [displayValues, additionalValues];
    }, [children]);

    const rootRef = useMergeRefs([forwardedRef, rovingContainerRef]);

    const formatMessageAriaLabel = intl.formatMessage(
      {
        id: '4ER0rfN8lI3HtaFd',
        defaultMessage:
          'Group of Avatars. {hiddenAvatars} hidden, {totalAvatars} total users',
        description: 'Label for the collapsed Avatars.',
      },
      {
        hiddenAvatars: additionalValues.length,
        totalAvatars: additionalValues.length + displayValues.length,
      },
    );

    return displayValues.length ? (
      <Flex
        gap={4}
        alignItems="center"
        ref={rootRef}
        className={clsx(focusRingStatic(), consumerClassName)}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        aria-label={ariaLabel ?? formatMessageAriaLabel}
        {...remainingAriaLabelingProps}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      >
        <Flex alignItems="center" gap={0}>
          {displayValues}
        </Flex>

        {additionalValues.length > 0 && (
          <Menu>
            <Menu.Trigger>
              <Button
                size="condensed"
                variant="emphasized"
                className={clsx(
                  'rovingFocusElement',
                  consumerClassName,
                  styles.additionalAvatarWrapper,
                )}
              >
                +{additionalValues.length}
              </Button>
            </Menu.Trigger>
            <Menu.Content
              data-dtrum-mask={dataDtrumMask}
              data-dtrum-allow={dataDtrumAllow}
            >
              {additionalValues}
            </Menu.Content>
          </Menu>
        )}
      </Flex>
    ) : null;
  },
);

(
  RootAvatarGroup as typeof RootAvatarGroup & { displayName: string }
).displayName = COMPONENT_NAME;

/**
 * The `Avatar` component can be used as a visual representation of multiple users.
 * @public
 */
export const AvatarGroup = Object.assign(RootAvatarGroup, {
  Item,
});
